import type { EffectRegion } from "@client/types/vision";
import { getRegionBounds, isInRegion } from "./regionMask";

/**
 * Apply contrast adjustment to image data
 * @param imageData - ImageData to modify
 * @param contrast - Contrast level (1 = no change)
 * @param mode - Optional color mode adjustment
 * @returns Modified ImageData
 */
export function applyContrastToImageData(
	imageData: ImageData,
	contrast: number,
	mode?: "grayscale" | "warm" | "cool",
	region?: EffectRegion | null,
): ImageData {
	const data = imageData.data;
	const factor =
		(259 * (contrast * 100 + 255)) / (255 * (259 - contrast * 100));

	if (region) {
		const width = imageData.width;
		const bounds = getRegionBounds(region);
		const rx1 = Math.max(0, Math.floor(bounds.rx1));
		const ry1 = Math.max(0, Math.floor(bounds.ry1));
		const rx2 = Math.min(imageData.width, Math.ceil(bounds.rx2));
		const ry2 = Math.min(imageData.height, Math.ceil(bounds.ry2));
		const isPolygon = region.type === "polygon";

		for (let y = ry1; y < ry2; y++) {
			for (let x = rx1; x < rx2; x++) {
				if (isPolygon && !isInRegion(x, y, region)) continue;
				const i = (y * width + x) * 4;
				const r = data[i];
				const g = data[i + 1];
				const b = data[i + 2];
				if (r !== undefined && g !== undefined && b !== undefined) {
					let newR = factor * (r - 128) + 128;
					let newG = factor * (g - 128) + 128;
					let newB = factor * (b - 128) + 128;

					if (mode === "grayscale") {
						const gray = 0.299 * newR + 0.587 * newG + 0.114 * newB;
						newR = newG = newB = gray;
					} else if (mode === "warm") {
						newR = Math.min(255, newR * 1.2);
						newB = Math.max(0, newB * 0.8);
					} else if (mode === "cool") {
						newR = Math.max(0, newR * 0.8);
						newB = Math.min(255, newB * 1.2);
					}

					data[i] = Math.min(255, Math.max(0, newR));
					data[i + 1] = Math.min(255, Math.max(0, newG));
					data[i + 2] = Math.min(255, Math.max(0, newB));
				}
			}
		}
		return imageData;
	}

	for (let i = 0; i < data.length; i += 4) {
		const r = data[i];
		const g = data[i + 1];
		const b = data[i + 2];
		if (r !== undefined && g !== undefined && b !== undefined) {
			let newR = factor * (r - 128) + 128;
			let newG = factor * (g - 128) + 128;
			let newB = factor * (b - 128) + 128;

			// Apply mode-specific adjustments
			if (mode === "grayscale") {
				const gray = 0.299 * newR + 0.587 * newG + 0.114 * newB;
				newR = newG = newB = gray;
			} else if (mode === "warm") {
				// Warm filter: increase red, decrease blue
				newR = Math.min(255, newR * 1.2);
				newB = Math.max(0, newB * 0.8);
			} else if (mode === "cool") {
				// Cool filter: increase blue, decrease red
				newR = Math.max(0, newR * 0.8);
				newB = Math.min(255, newB * 1.2);
			}

			data[i] = Math.min(255, Math.max(0, newR)); // Red
			data[i + 1] = Math.min(255, Math.max(0, newG)); // Green
			data[i + 2] = Math.min(255, Math.max(0, newB)); // Blue
			// Alpha channel remains unchanged
		}
	}
	return imageData;
}
